import {Component, EventEmitter, Input, OnInit, Optional, Output} from '@angular/core';
import {Translatable, TranslationService, DateFormatService, DATE_FORMAT_YMD} from '@ngmedax/translation';

import {MasterDataLayoutService} from '../../services/master-data-layout.service';
import {TRANSLATION_RENDERER_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {ClientGridRendererConfig, GridRendererFieldEntry, CustomFieldDefinition} from '../../../../types';


// hack to inject decorator declarations
export interface ClientGridRendererComponent extends Translatable {}

/**
 * Reusable component for rendering patient data in grid columns.
 * Renders display fields as a linked badge and detail fields as muted text,
 * based on the configurable ClientGridRendererConfig from the settings service.
 */
@Component({
  selector: 'ngmedax-client-grid-renderer',
  templateUrl: './client-grid-renderer.component.html',
  styleUrls: ['./client-grid-renderer.component.css']
})
@Translatable({scope: TRANSLATION_RENDERER_SCOPE, keys: KEYS})
export class ClientGridRendererComponent implements OnInit {
  /**
   * The client object containing patient data.
   */
  @Input() public client: any;

  /**
   * The client uid for routing / event emission.
   */
  @Input() public clientId: string;

  /**
   * Whether the patient link is clickable.
   */
  @Input() public canEdit = false;

  /**
   * Link behavior: 'route' navigates via routerLink, 'event' emits patientEdit.
   */
  @Input() public linkMode: 'route' | 'event' = 'route';

  /**
   * Emits clientId when linkMode is 'event' and user clicks the patient link.
   */
  @Output() public patientEdit = new EventEmitter<string>();

  /**
   * Resolved display field values for rendering.
   */
  public displayValues: {label?: string; value: string}[] = [];

  /**
   * Resolved detail field values for rendering.
   */
  public detailValues: {label?: string; value: string}[] = [];

  /**
   * Effective grid renderer config.
   */
  private config: ClientGridRendererConfig = null;

  /**
   * Custom field definitions for resolving custom field labels.
   */
  private customFieldDefinitions: CustomFieldDefinition[] = [];

  /**
   * Injects dependencies.
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    @Optional() private dateFormatService: DateFormatService,
    private masterDataLayoutService: MasterDataLayoutService
  ) {
  }

  /**
   * Loads config and resolves field values on init.
   */
  public async ngOnInit() {
    const [customConfig, definitions] = await Promise.all([
      this.masterDataLayoutService.getOrLoadGridRendererConfig(),
      this.masterDataLayoutService.loadCustomFieldDefinitions()
    ]);

    this.customFieldDefinitions = definitions;
    this.config = this.masterDataLayoutService.getEffectiveGridRendererConfig(customConfig);
    this.resolveValues();
  }

  /**
   * Returns the routerLink array for route mode.
   */
  public getRouterLink(): any[] | null {
    if (this.linkMode !== 'route' || !this.canEdit || !this.clientId) {
      return null;
    }
    return ['/module/patient/crud', this.clientId];
  }

  /**
   * Handles click on the patient badge in event mode.
   */
  public onBadgeClick(): void {
    if (this.linkMode === 'event' && this.canEdit) {
      this.patientEdit.emit(this.clientId);
    }
  }

  /**
   * Returns true if client is anonymous.
   */
  public get isAnonymous(): boolean {
    return this.client && this.client.anonymous;
  }

  /**
   * Returns true if client is pseudonymous.
   */
  public get isPseudonymous(): boolean {
    return this.client && this.client.pseudonymous;
  }

  /**
   * Returns the pseudo id display text.
   */
  public get pseudoIdText(): string {
    if (!this.client || !this.client.pseudoId) {
      return '';
    }
    return '- Pseudo Id: ' + this.client.pseudoId;
  }

  /**
   * Resolves field entries to display values based on the client object.
   */
  private resolveValues(): void {
    if (!this.client || !this.config) {
      this.displayValues = [];
      this.detailValues = [];
      return;
    }

    this.displayValues = this.resolveFieldValues(this.config.displayFields);
    this.detailValues = this.resolveFieldValues(this.config.detailFields);
  }

  /**
   * Resolves an array of field entries to label/value pairs.
   */
  private resolveFieldValues(fields: GridRendererFieldEntry[]): {label?: string; value: string}[] {
    if (!fields) {
      return [];
    }

    return fields
      .map(entry => {
        const value = this.getFieldValue(entry);
        if (value === null || value === undefined || value === '') {
          return null;
        }
        return {
          label: entry.label || undefined,
          value: String(value)
        };
      })
      .filter(v => v !== null);
  }

  /**
   * Gets the display value for a field entry from the client object.
   */
  private getFieldValue(entry: GridRendererFieldEntry): string {
    if (!this.client) {
      return '';
    }

    if (entry.fieldType === 'custom') {
      return this.getCustomFieldValue(entry.fieldKey);
    }

    return this.getStandardFieldValue(entry.fieldKey);
  }

  /**
   * Gets the display value for a standard field.
   */
  private getStandardFieldValue(fieldKey: string): string {
    const raw = this.client[fieldKey];
    if (raw === null || raw === undefined || raw === '') {
      return '';
    }

    // special formatting for specific fields
    if (fieldKey === 'birthDate') {
      return this.formatDate(raw);
    }

    if (fieldKey === 'gender') {
      return this.formatGender(raw);
    }

    return String(raw);
  }

  /**
   * Gets the display value for a custom field from client.custom.
   */
  private getCustomFieldValue(fieldKey: string): string {
    if (!this.client.custom || this.client.custom[fieldKey] === null || this.client.custom[fieldKey] === undefined) {
      return '';
    }
    return String(this.client.custom[fieldKey]);
  }

  /**
   * Formats a MySQL date string to the locale date format.
   */
  private formatDate(date: string): string {
    if (!this.dateFormatService || !date) {
      return date || '';
    }

    const format = this.getDateFormat ? this.getDateFormat(DATE_FORMAT_YMD) : DATE_FORMAT_YMD;
    return this.dateFormatService.fromMySqlDate(date, format);
  }

  /**
   * Formats gender to short form.
   */
  private formatGender(gender: string): string {
    if (gender === 'male') {
      return 'm';
    }
    if (gender === 'female') {
      return 'w';
    }
    if (gender === 'diverse') {
      return 'd';
    }
    return gender;
  }
}
